import { Dispatch, SetStateAction, useEffect, useMemo } from 'react'

import { useUserProfile } from 'src/layout/hooks'

import { JSONStringify } from '../temporalHelpers'

import useUrlState, { IQueryParamsObject } from './useUrlState'

export const PERSISTANT_FILTERS_KEY = 'plato:persistant_filters'

export const parser = function (value: string) {
  return JSON.parse(value)
}

/**
 * Helper function that handles the update of the fields of a JSON object
 */
export const updateFields = (
  localStorageKey: string,
  newFieldValues: Record<string, any>,
  defaultValues: Record<string, any>
): Record<string, any> => {
  const json = localStorage.getItem(localStorageKey) || ''
  if (!json) {
    return defaultValues
  }

  const parsedJson = parser(json)
  const fields = Object.keys(newFieldValues)
  for (const field of fields) {
    parsedJson[field] = newFieldValues[field]
  }
  return parsedJson
}

/**
 * Equivalent to `React.useState` but stores the state in the URL and in the localstorage
 *
 * At component mount the value is from (in that order):
 * - current URL
 * - localStorage
 * - defaultValue
 */
export const useURLAndStorageState = function <State extends IQueryParamsObject>(
  key: string,
  defaultValue: State,
  jsonFields?: string[],
  newFieldValues?: Record<string, any>
): [State, Dispatch<SetStateAction<State>>] {
  const {
    id: userID,
    jurisdiction: { agency_key },
  } = useUserProfile()
  const defaultStateJSON = useMemo(() => JSONStringify(defaultValue), [defaultValue])
  const localStorageKey = useMemo(
    () => `${PERSISTANT_FILTERS_KEY}_${userID}_${agency_key}_${key}`,
    [PERSISTANT_FILTERS_KEY, userID, key, agency_key]
  )

  let updatedJson = {}
  if (newFieldValues) {
    updatedJson = updateFields(localStorageKey, newFieldValues, defaultValue)
  }

  const [state, setState] = useUrlState<State>(
    newFieldValues ? updatedJson : parser(localStorage.getItem(localStorageKey) ?? defaultStateJSON),
    jsonFields
  )

  useEffect(() => {
    localStorage.setItem(localStorageKey, JSONStringify(state))
  }, [key, state])

  return [state, setState]
}
